import React from "react"
import { Button, Row, Col, Space, Typography, Tag, Alert } from "antd"
import { 
  RollbackOutlined, 
  InboxOutlined, 
  UploadOutlined, 
  DollarOutlined, 
  FileTextOutlined, 
  AuditOutlined, 
  FileProtectOutlined 
} from "@ant-design/icons"
import { getInventoryFulfillmentInfo, isExitMovement } from "../../utils/reportUtils"
import { IMovementRS } from "../../models/storageModel"
import { ReceiptData } from "../ViewMovementDetail"
import InvoicePDFDoc from "../InvoicePDFDoc"
import ReceiptPDFPrint from "../ReceiptPDFPrint"

const { Title, Text } = Typography

export interface MovementDetailHeaderBarProps {
  movement: Partial<IMovementRS>
  labels: { title: string; counterparty: string; user: string }
  docType: string
  isPreparatory: boolean
  isPaidInFull: boolean
  hasPendingFulfillment?: boolean
  documentData: ReceiptData
  onGoBack: () => void
  onOpenFulfillModal: () => void
  onOpenPaymentModal: () => void
}

export const getDocumentStatus = (documentStatus: string) => {
  switch (documentStatus) {
    case 'COMPLETED':
      return 'Completado'
    case 'PENDING':
      return 'Pendiente'
    case 'APPROVED':
      return 'Aprobado'
    case 'CANCELLED':
    case 'VOIDED':
      return 'Anulado'
    case 'DRAFT':
      return 'Borrador'
    case 'REJECTED':
      return 'Rechazado'
    default:
      return documentStatus ? documentStatus.replace(/_/g, ' ') : 'Estado'
  }
}

const MovementDetailHeaderBar: React.FC<MovementDetailHeaderBarProps> = ({
  movement,
  labels,
  docType,
  isPreparatory,
  isPaidInFull,
  hasPendingFulfillment,
  documentData,
  onGoBack,
  onOpenFulfillModal,
  onOpenPaymentModal,
}) => {
  return (
    <>
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Space align="center" wrap>
            <Button icon={<RollbackOutlined />} onClick={onGoBack} />
            <Title level={3} style={{ margin: 0 }}>
              {labels.title} {movement.document_number}
            </Title>
            <Tag color="green">{getDocumentStatus(movement.status || "")}</Tag>
            {isPreparatory ? (
              <Tag 
                color="geekblue" 
                icon={
                  docType === 'QUOTATION' ? (
                    <FileProtectOutlined />
                  ) : docType === 'PURCHASE_REQUEST' ? (
                    <AuditOutlined />
                  ) : (
                    <FileTextOutlined />
                  )
                }
                style={{ fontSize: 12, padding: '2px 8px' }}
              >
                {docType === 'QUOTATION' 
                  ? 'Cotización (Propuesta Comercial)' 
                  : docType === 'PURCHASE_REQUEST' 
                  ? 'Solicitud Interna (Administrativa)' 
                  : 'Orden de Compra (Comercial)'}
              </Tag>
            ) : (
              <>
                {movement.execution_mode === "DEFERRED" ? (
                  <Tag color="purple">Flujo Diferido</Tag>
                ) : (
                  <Tag color="blue">Flujo Inmediato</Tag>
                )}
                {(() => {
                  const fInfo = getInventoryFulfillmentInfo(
                    docType || movement.document_type || "", 
                    movement.reception_status
                  )
                  return (
                    <Tag color={fInfo.color}>
                      {fInfo.iconType === 'delivery' ? '📤' : fInfo.iconType === 'transfer' ? '🔄' : '📥'} {fInfo.label}
                    </Tag>
                  )
                })()}
                {(movement.payment_status === "PAID" || isPaidInFull) ? (
                  <Tag color="success">💳 Pago: Totalmente Pagado</Tag>
                ) : movement.payment_status === "PARTIALLY_PAID" ? (
                  <Tag color="warning">💳 Pago: Abonado Parcial</Tag>
                ) : (
                  <Tag color="error">💳 Pago: Pendiente</Tag>
                )}
                {(movement.settlement_status === "SETTLED" || (movement.reception_status === "RECEIVED" && isPaidInFull)) ? (
                  <Tag color="cyan">✓ Liquidado</Tag>
                ) : (
                  <Tag color="processing">⏳ Abierto</Tag>
                )}
              </>
            )}
          </Space>
        </Col>
        <Col>
          <Space wrap>
            {hasPendingFulfillment && (
              <Button
                type="primary"
                icon={isExitMovement(docType) ? <UploadOutlined /> : <InboxOutlined />}
                style={{ backgroundColor: "#1890ff" }}
                onClick={onOpenFulfillModal}
              >
                {isExitMovement(docType) ? "Despachar Mercancía" : "Dar Entrada a Bodega"}
              </Button>
            )}
            {!isPreparatory && !isPaidInFull && (
              <Button
                type="primary"
                icon={<DollarOutlined />}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                onClick={onOpenPaymentModal}
              >
                Registrar Abono / Pago
              </Button>
            )}
            {docType === 'SALE_INVOICE' && <ReceiptPDFPrint receipt={documentData} />}
            <InvoicePDFDoc invoice={documentData} />
          </Space>
        </Col>
      </Row>

      {isPreparatory && (
        <Alert
          message={
            <Text strong style={{ fontSize: 13 }}>
              {docType === 'PURCHASE_REQUEST'
                ? "Documento Administrativo: Solicitud de Compra Interna"
                : docType === 'PURCHASE_ORDER'
                ? "Documento Comercial: Orden de Compra a Proveedor"
                : "Propuesta Comercial: Cotización a Cliente"}
            </Text>
          }
          description={
            docType === 'PURCHASE_REQUEST'
              ? "Este documento formaliza un requerimiento de compras o insumos. No genera compromisos de pago, cartera ni ingresos físicos al inventario de almacén."
              : docType === 'PURCHASE_ORDER'
              ? "Este pedido formaliza la orden de compra emitida al proveedor. No altera el stock de almacén ni genera obligaciones de cartera hasta su recepción física."
              : "Esta cotización presenta una propuesta comercial con precios y condiciones para el cliente. No descuenta inventario ni genera cartera."
          }
          type="info"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}
    </>
  )
}

export default MovementDetailHeaderBar
